import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Send, Users, HeartPulse, AlertTriangle, Handshake, Loader2 } from "lucide-react";

const money = (n) => "$" + Math.round(n || 0).toLocaleString();
const FREQ = [["daily", "Daily"], ["weekly", "Weekly (Mon)"], ["monthly", "Monthly (1st)"], ["off", "Never"]];
const CATS = [["portfolio_digest", "Portfolio digest", "Scheduled summary across the workspace"], ["company_health", "Health alerts", "Spikes in overdue work or stalled pipeline"], ["deal_won", "Deal won", "When a deal is closed won"], ["staff_activity", "Staff activity", "Tasks created & completed by the team"], ["client_health", "Client health", "Clients and at-risk signals"], ["outstanding_issues", "Outstanding issues", "Overdue tasks and stalled deals"]];

const Stat = ({ icon: Icon, label, value, tone }) => (
  <div className="panel p-4"><div className="flex items-center gap-1.5 text-xs text-slate-400 mb-1"><Icon className="w-3.5 h-3.5" /> {label}</div><div className={cn("font-heading text-2xl font-semibold tabular", tone === "warn" && "text-amber-600")}>{value}</div></div>
);

export default function Reports() {
  const [sum, setSum] = useState(null);
  const [settings, setSettings] = useState(null);
  const [recips, setRecips] = useState("");
  const [sending, setSending] = useState(false);

  const loadSettings = () => api.get("/reports/settings").then((r) => { setSettings(r.data); setRecips((r.data.extra_recipients || []).join(", ")); }).catch(() => {});
  useEffect(() => { api.get("/reports/summary").then((r) => setSum(r.data)).catch((e) => toast.error(apiError(e))); loadSettings(); }, []);

  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try { await api.put("/reports/settings", patch); } catch (e) { toast.error(apiError(e)); loadSettings(); }
  };
  const toggleCat = (k, v) => saveSettings({ categories: { ...(settings.categories || {}), [k]: v } });
  const saveRecips = () => saveSettings({ extra_recipients: recips.split(",").map((x) => x.trim()).filter(Boolean) }).then(() => toast.success("Recipients saved"));
  const sendNow = async () => { setSending(true); try { const { data } = await api.post("/reports/send"); toast.success(`Report sent to ${data.sent}/${data.recipients} recipient(s)`); } catch (e) { toast.error(apiError(e)); } finally { setSending(false); } };

  if (!sum || !settings) return <div className="text-sm text-slate-400 p-8">Loading…</div>;

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports" subtitle="Staff activity, client health and outstanding issues — for this workspace">
        <Button onClick={sendNow} disabled={sending} data-testid="report-send-now">{sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Send now</Button>
      </PageHeader>

      <p className="eyebrow mb-2">Staff activity (7 days)</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Stat icon={Users} label="Active users" value={sum.staff.users} />
        <Stat icon={Users} label="Tasks completed" value={sum.staff.tasks_completed_7d} />
        <Stat icon={Users} label="Tasks created" value={sum.staff.tasks_created_7d} />
        <Stat icon={Handshake} label="Won this week" value={money(sum.pipeline.won_value_7d)} />
      </div>
      <p className="eyebrow mb-2">Client health &amp; outstanding issues</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat icon={HeartPulse} label="Clients" value={sum.clients.total} />
        <Stat icon={AlertTriangle} label="At-risk clients" value={sum.clients.at_risk} tone={sum.clients.at_risk ? "warn" : null} />
        <Stat icon={AlertTriangle} label="Overdue tasks" value={sum.issues.overdue_tasks} tone={sum.issues.overdue_tasks ? "warn" : null} />
        <Stat icon={AlertTriangle} label="Stalled deals" value={sum.issues.stalled_deals} tone={sum.issues.stalled_deals ? "warn" : null} />
      </div>

      <div className="panel p-5 space-y-5" data-testid="report-settings">
        <div>
          <p className="eyebrow mb-2">Email frequency</p>
          <div className="flex rounded-lg border p-0.5 text-xs w-fit" style={{ borderColor: "var(--line)" }}>
            {FREQ.map(([k, l]) => <button key={k} onClick={() => saveSettings({ frequency: k })} className={cn("px-3 py-1.5 rounded-md transition-colors", settings.frequency === k ? "bg-slate-900 text-white" : "text-slate-500")} data-testid={`report-freq-${k}`}>{l}</button>)}
          </div>
        </div>
        <div>
          <p className="eyebrow mb-2">What gets emailed</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {CATS.map(([k, label, desc]) => (
              <div key={k} className="flex items-center justify-between gap-3 panel p-3">
                <div className="min-w-0"><div className="text-sm font-medium text-slate-800">{label}</div><div className="text-xs text-slate-500 truncate">{desc}</div></div>
                <Switch checked={!!(settings.categories || {})[k]} onCheckedChange={(v) => toggleCat(k, v)} data-testid={`report-cat-${k}`} />
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="eyebrow mb-2">Extra recipients</p>
          <p className="text-xs text-slate-500 mb-1.5">Everyone with a reporting role is always included. Add more addresses (comma-separated).</p>
          <div className="flex gap-2"><Input value={recips} onChange={(e) => setRecips(e.target.value)} placeholder="ops@you.com, advisor@you.com" data-testid="report-recipients" /><Button variant="outline" onClick={saveRecips} data-testid="report-recipients-save">Save</Button></div>
        </div>
      </div>
    </div>
  );
}
